import * as pdfjsLib from 'pdfjs-dist';

// pdfjs-dist 4.x uses Promise.withResolvers (missing in some older runtimes).
if (typeof Promise.withResolvers !== 'function') {
  Promise.withResolvers = function withResolvers() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// Worker file is copied next to bundle.js by webpack (PdfWorkerCopyPlugin).
// In production, STATIC_URL points at GCS — do NOT resolve against window.location
// (console.roomnexa.com/static/... 404s; GCS has the file).
const webpackPublicPath =
  (typeof __webpack_public_path__ === 'string' && __webpack_public_path__)
  || '/static/frontend/';

function joinUrl(base, path) {
  const left = String(base || '').replace(/\/?$/, '/');
  const right = String(path || '').replace(/^\//, '');
  return `${left}${right}`;
}

function workerSrcFromBundleScript() {
  if (typeof document === 'undefined') return null;
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i -= 1) {
    const src = scripts[i].src || '';
    if (!src) continue;
    if (src.includes('/frontend/bundle.js') || /\/bundle\.js(\?|$)/.test(src)) {
      return src.replace(/bundle\.js(\?.*)?$/i, 'pdf.worker.min.mjs');
    }
  }
  return null;
}

function resolveWorkerSrc() {
  // 1) Same host/path as the loaded bundle.js (GCS in production).
  const fromBundle = workerSrcFromBundleScript();
  if (fromBundle) return fromBundle;

  // 2) Django-injected STATIC_URL (also GCS in production).
  const staticUrl = typeof window !== 'undefined'
    ? (window.DJANGO_CONTEXT?.staticUrl || window.DJANGO_CONTEXT?.STATIC_URL || '')
    : '';
  if (staticUrl) {
    return joinUrl(staticUrl, 'frontend/pdf.worker.min.mjs');
  }

  // 3) Local / relative webpack publicPath.
  const relative = `${webpackPublicPath}pdf.worker.min.mjs`;
  if (typeof window === 'undefined' || !window.location?.origin) {
    return relative;
  }
  try {
    return new URL(relative, window.location.origin).href;
  } catch {
    return relative;
  }
}

// Always refresh workerSrc — prior builds may have cached a bad absolute console URL.
pdfjsLib.GlobalWorkerOptions.workerSrc = resolveWorkerSrc();

function looksLikePdfBytes(bytes) {
  if (!bytes || bytes.byteLength < 5) return false;
  const header = String.fromCharCode(
    bytes[0],
    bytes[1],
    bytes[2],
    bytes[3],
    bytes[4],
  );
  return header === '%PDF-';
}

/**
 * Load a PDF for preview. Prefer main-thread bytes for blob:/data: URLs so the
 * worker does not need to fetch them (a common "Preview unavailable" failure).
 */
export async function loadPdfDocument(pdfUrl, options = {}) {
  if (!pdfUrl) {
    throw new Error('Missing PDF url');
  }

  // Re-resolve in case DJANGO_CONTEXT / script tags were not ready at module init.
  pdfjsLib.GlobalWorkerOptions.workerSrc = resolveWorkerSrc();

  const src = String(pdfUrl);
  const isInlineUrl = src.startsWith('blob:') || src.startsWith('data:');

  if (isInlineUrl) {
    const response = await fetch(src);
    if (!response.ok) {
      throw new Error(`Failed to read PDF blob (${response.status})`);
    }
    const buffer = await response.arrayBuffer();
    const data = new Uint8Array(buffer);
    if (!looksLikePdfBytes(data)) {
      throw new Error('Response is not a valid PDF');
    }
    return pdfjsLib.getDocument({
      data,
      useWorkerFetch: false,
      ...options,
    }).promise;
  }

  return pdfjsLib.getDocument({
    url: src,
    withCredentials: true,
    useWorkerFetch: false,
    ...options,
  }).promise;
}

export { pdfjsLib };
